/**
 * One request's page (Bill 2026-11-04). For your own report: the plain briefing the Risk panel's Compliance reviews
 * shows (what it is, why, what was done, what is left, history), composed by the shared trackerBriefing words in
 * @retail-ops/support-report from the record pcb-retail-api cut down for you (no compliance notes, no
 * requested-change text). For anyone else's report: where it stands, at summary level only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError } from '@retail-ops/api-client';
import { briefing, historyLines, type BriefLine } from '@retail-ops/support-report';
import { Banner, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { RequestDetailResponse } from './pilotApi';
import { STAGE_TONE, shortDay } from './pilotModel';

function Lines({ lines }: { lines: ReadonlyArray<BriefLine> }): JSX.Element {
  return (
    <ul>
      {lines.map((l, i) => (
        <li key={i}>
          {l.url ? <a href={l.url} target="_blank" rel="noreferrer">{l.text}</a> : l.text}
          {l.detail && <span className="pilot-brief__detail">{l.detail}</span>}
        </li>
      ))}
    </ul>
  );
}

export function RequestDetail(): JSX.Element {
  const { key = '' } = useParams();
  const client = useApiClient();
  const navigate = useNavigate();
  const [data, setData] = useState<RequestDetailResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setErr(null);
    void (async () => {
      try {
        const r = await client.http.get<RequestDetailResponse>(`/v3/rop/my-requests/${encodeURIComponent(key)}`);
        if (!cancelled) setData(r);
      } catch (e: unknown) {
        if (!cancelled) setErr(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [client, key]);

  const brief = useMemo(() => (data?.briefing ? briefing(data.briefing) : null), [data]);
  const history = useMemo(() => (data?.briefing ? historyLines(data.briefing.events) : []), [data]);
  const it = data?.item;

  return (
    <section aria-label={`Request ${key}`}>
      <button type="button" className="pilot-back" onClick={() => navigate('/pilot')}>← All requests</button>
      {err && <Banner tone="err" title="Couldn't load this request">{err}</Banner>}
      {!err && !data && <div className="pilot-empty">Loading…</div>}
      {it && (
        <>
          <div className="pilot-brief">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>{it.key}: {it.summary}</h2>
              <span className="pilot-row__pills">
                {it.awaitingCompliance && <Pill variant="warn">Waiting on compliance review</Pill>}
                <Pill variant={STAGE_TONE[it.stage] ?? 'muted'}>{it.stageLabel}</Pill>
              </span>
            </div>
            <p className="pilot-row__meta">
              Reported {shortDay(it.requestedAt)} by {it.mine ? 'you' : it.requestedByName}
              {it.category ? ` · ${it.category.charAt(0).toUpperCase()}${it.category.slice(1)}` : ''}
              {it.fixedAt ? ` · Live ${shortDay(it.fixedAt)}` : ''}
              {it.closedAt ? ` · Closed ${shortDay(it.closedAt)}` : ''}
            </p>
          </div>

          {data?.briefingNote && <Banner tone="info">{data.briefingNote}</Banner>}

          {brief && (
            <>
              <div className="pilot-brief"><h3>What it is</h3><Lines lines={brief.whatItIs} /></div>
              <div className="pilot-brief"><h3>Why</h3><Lines lines={brief.why} /></div>
              <div className="pilot-brief"><h3>What was done</h3><Lines lines={brief.done} /></div>
              <div className="pilot-brief">
                <h3>What is left</h3>
                {brief.notDone.length ? <Lines lines={brief.notDone} /> : <p className="pilot-muted">Nothing: it is finished.</p>}
              </div>
              <div className="pilot-brief">
                <h3>History</h3>
                {history.length === 0 ? <p className="pilot-muted">No history recorded yet.</p> : (
                  <ul className="pilot-history">
                    {history.map((h) => (
                      <li key={h.id}>
                        {h.what}
                        <div className="pilot-history__who">{h.when} · {h.who}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
